/**
 * /try — public 60-second token-savings live demo.
 *
 * v35.31 (Feb 2026)  ·  The wedge demo.
 *
 * Visitor pastes a prompt, picks a model, clicks Compare → sees a real
 * side-by-side before/after token count + dollar delta, computed by our
 * actual production Prompt Compactor (not a mock).
 *
 * No auth, no signup, no email gate. The whole point is "show, don't tell."
 * Below the diff we show:
 *   • Exactly which techniques fired (compaction always; cache/cascade
 *     are noted as "production averages")
 *   • A transparency note that explains where the 50-70% figure comes from
 *   • Two CTAs — "start free trial" + "view the Auto-Compiler proxy code"
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowRight, Zap, TrendingDown, Check, Loader2, AlertCircle, Code,
  Database, Cpu, ArrowLeft, Volume2, Square,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_PROMPT = `You are a helpful AI assistant. You are very smart and you always think step by step. You think step by step. You always reply in clean markdown. Always reply in markdown. The user is a senior software engineer.

The user has the following code:

# Helper function that returns the total price
# This function loops through all items
# and adds up the price
def calculate_total(items):
    # initialise total to zero
    total = 0
    for item in items:
        # add each item's price
        total += item.price
    return total

# Another helper
def apply_tax(amount, rate):
    # tax rate must be a decimal
    return amount * (1 + rate)

Please review the code carefully. Please look for bugs carefully. Be thorough. Be thorough. Suggest improvements. Suggest improvements. Look at performance. Look at edge cases. Be helpful. Be helpful. Reply in markdown. Reply in markdown.`;

const MODELS = [
  { id: 'gpt-5.2',           label: 'OpenAI · GPT-5.2',          provider: 'OpenAI' },
  { id: 'gpt-5.2-mini',      label: 'OpenAI · GPT-5.2 mini',     provider: 'OpenAI' },
  { id: 'claude-sonnet-4.6', label: 'Anthropic · Sonnet 4.6',    provider: 'Anthropic' },
  { id: 'claude-haiku-4.5',  label: 'Anthropic · Haiku 4.5',     provider: 'Anthropic' },
  { id: 'gemini-3-pro',      label: 'Google · Gemini 3 Pro',     provider: 'Google' },
  { id: 'gemini-3-flash',    label: 'Google · Gemini 3 Flash',   provider: 'Google' },
  { id: 'groq-llama-3.3',    label: 'Groq · Llama 3.3 70B',      provider: 'Groq' },
  { id: 'mistral-large',     label: 'Mistral · Large',           provider: 'Mistral' },
];

const fmtUsd6 = (n) => {
  const v = Number(n || 0);
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(6)}`;
};
const fmtTokens = (n) => Number(n || 0).toLocaleString();
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;

export default function TryDemo() {
  const [prompt, setPrompt]   = useState(SAMPLE_PROMPT);
  const [model, setModel]     = useState('gpt-5.2');
  const [output, setOutput]   = useState(300);
  const [result, setResult]   = useState(null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [voiceState, setVoiceState] = useState('idle'); // idle | loading | playing
  const audioRef = useRef(null);

  const narrate = async () => {
    if (!result) return;
    if (voiceState === 'playing') {
      audioRef.current?.pause();
      audioRef.current = null;
      setVoiceState('idle');
      return;
    }
    setVoiceState('loading');
    try {
      const { data } = await axios.post(`${API}/voice/narrate-demo`, {
        model: result.model,
        before_tokens: result.before.input_tokens,
        after_tokens: result.after.input_tokens,
        tokens_saved_pct: result.savings.tokens_saved_pct,
        dollars_saved: result.savings.dollars_saved,
        monthly_saved: result.savings.dollars_saved * 1_000_000,
      }, { responseType: 'blob' });
      const audio = new Audio(URL.createObjectURL(data));
      audioRef.current = audio;
      audio.onended = () => setVoiceState('idle');
      await audio.play();
      setVoiceState('playing');
    } catch {
      setVoiceState('idle');
    }
  };

  const runCompare = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/try/token-savings`, {
        prompt,
        model,
        estimated_output_tokens: Number(output) || 300,
      });
      setResult(data);
      // Smooth scroll to result
      setTimeout(() => {
        document.getElementById('result-card')?.scrollIntoView({
          behavior: 'smooth', block: 'start',
        });
      }, 100);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Demo failed — please try a shorter prompt.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="Try CARTIEAI live — see your AI tokens drop 60% in 60 seconds"
        description="Paste any prompt. Pick any model — OpenAI, Anthropic, Gemini, Groq, Mistral. See real before/after token counts + dollar savings, computed by our production Prompt Compactor. No signup."
        canonical="https://cartieai.com/try"
        ogSlug="try"
        type="website"
      />

      {/* ── Hero ── */}
      <section className="border-b border-slate-900">
        <div className="max-w-5xl mx-auto px-6 pt-12 pb-10">
          <Link to="/"
                className="inline-flex items-center text-xs text-slate-400 hover:text-slate-100 mb-6"
                data-testid="try-back-home">
            <ArrowLeft className="w-3 h-3 mr-1" /> CARTIEAI · home
          </Link>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-500/30 rounded-full px-3 py-1 mb-5">
            <Zap className="w-3.5 h-3.5" /> Live demo · No signup · Runs in your browser
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-4" data-testid="try-h1">
            See your AI tokens drop{' '}
            <span className="text-emerald-300">60%</span>.
            <span className="block text-slate-300 text-2xl sm:text-3xl mt-3">
              In 60 seconds. On your own prompt.
            </span>
          </h1>
          <p className="text-base text-slate-400 max-w-2xl">
            Paste any prompt below — your real system message, RAG context, code-review
            instruction, agent loop step, anything. Pick a model. Click <em>Compare</em>.
            You&apos;ll see the actual before/after token count from <strong>our production
            compactor</strong> — same code that runs on every customer&apos;s LLM call.
          </p>
        </div>
      </section>

      {/* ── Demo form ── */}
      <section className="max-w-5xl mx-auto px-6 py-10">
        <Card className="bg-slate-900/40 ring-1 ring-slate-800 overflow-hidden"
              data-testid="try-form-card">
          <CardContent className="p-6 space-y-5">
            <div>
              <label htmlFor="try-prompt" className="block text-xs uppercase tracking-wider text-emerald-300 mb-2">
                Your prompt
              </label>
              <Textarea
                id="try-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={10}
                spellCheck={false}
                placeholder="Paste your real LLM prompt here. The longer/more boilerplate, the bigger the savings."
                className="font-mono text-[13px] bg-slate-950 text-slate-100 placeholder:text-slate-500 caret-emerald-300 ring-1 ring-slate-800 focus:ring-emerald-500/40"
                data-testid="try-prompt-input"
              />
              <p className="text-[11px] text-slate-500 mt-1.5">
                {prompt.length} chars · ~{Math.ceil(prompt.length / 4)} tokens · Sample prompt pre-loaded so you can click Compare immediately.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="try-model" className="block text-xs uppercase tracking-wider text-emerald-300 mb-2">
                  Compare against pricing
                </label>
                <select
                  id="try-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg bg-slate-950 text-slate-100 ring-1 ring-slate-800 focus:ring-emerald-500/40 text-sm focus:outline-none"
                  data-testid="try-model-select"
                >
                  {MODELS.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="try-output" className="block text-xs uppercase tracking-wider text-emerald-300 mb-2">
                  Expected output tokens
                </label>
                <input
                  id="try-output"
                  type="number"
                  min={10}
                  max={8000}
                  value={output}
                  onChange={(e) => setOutput(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg bg-slate-950 text-slate-100 placeholder:text-slate-500 caret-emerald-300 ring-1 ring-slate-800 focus:ring-emerald-500/40 text-sm focus:outline-none font-mono"
                  data-testid="try-output-input"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Default 300 · adjust to match your real usage
                </p>
              </div>
            </div>

            <div className="pt-2">
              <Button
                size="lg"
                onClick={runCompare}
                disabled={busy || prompt.length < 10}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold w-full sm:w-auto"
                data-testid="try-compare-btn"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running compactor…</>
                ) : (
                  <>Compare <ArrowRight className="w-4 h-4 ml-1.5" /></>
                )}
              </Button>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 ring-1 ring-red-500/30 text-sm text-red-200"
                   data-testid="try-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* ── Result ── */}
      {result && (
        <section id="result-card" className="max-w-5xl mx-auto px-6 pb-10">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h2 className="text-xs uppercase tracking-wider text-emerald-300 inline-flex items-center gap-2">
              <TrendingDown className="w-3.5 h-3.5" /> Real before / after — computed live
            </h2>
            <button
              onClick={narrate}
              disabled={voiceState === 'loading'}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-200 bg-emerald-500/10 ring-1 ring-emerald-500/40 hover:bg-emerald-500/20 rounded-full px-3.5 py-1.5 transition-colors disabled:opacity-60"
              data-testid="try-narrate-btn"
            >
              {voiceState === 'loading' ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating voice…</>
              ) : voiceState === 'playing' ? (
                <><Square className="w-3 h-3" /> Stop</>
              ) : (
                <><Volume2 className="w-3.5 h-3.5" /> Hear your savings</>
              )}
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* BEFORE */}
            <Card className="bg-slate-900/40 ring-1 ring-slate-800" data-testid="try-before-card">
              <CardContent className="p-6">
                <div className="text-xs text-slate-500 mb-3">Direct to {result.model}</div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[11px] uppercase text-slate-500">Input tokens</div>
                    <div className="text-2xl font-mono text-slate-100" data-testid="try-before-input">
                      {fmtTokens(result.before.input_tokens)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-slate-500">Output tokens</div>
                    <div className="text-2xl font-mono text-slate-100">
                      {fmtTokens(result.before.output_tokens)}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-800">
                    <div className="text-[11px] uppercase text-slate-500">Cost per call</div>
                    <div className="text-3xl font-mono text-slate-100" data-testid="try-before-cost">
                      {fmtUsd6(result.before.cost_usd)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AFTER */}
            <Card className="bg-emerald-500/5 ring-1 ring-emerald-500/40" data-testid="try-after-card">
              <CardContent className="p-6">
                <div className="text-xs text-emerald-300 mb-3 inline-flex items-center gap-1.5">
                  <Zap className="w-3 h-3" /> Through CARTIE
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-[11px] uppercase text-emerald-300/70">Input tokens</div>
                    <div className="text-2xl font-mono text-emerald-300" data-testid="try-after-input">
                      {fmtTokens(result.after.input_tokens)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-emerald-300/70">Output tokens</div>
                    <div className="text-2xl font-mono text-emerald-300">
                      {fmtTokens(result.after.output_tokens)}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-emerald-500/20">
                    <div className="text-[11px] uppercase text-emerald-300/70">Cost per call</div>
                    <div className="text-3xl font-mono text-emerald-300" data-testid="try-after-cost">
                      {fmtUsd6(result.after.cost_usd)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Big savings banner */}
          <Card className="bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent ring-1 ring-emerald-500/40 mb-6"
                data-testid="try-savings-banner">
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-3 gap-6">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-300/80">Tokens saved (input)</div>
                  <div className="text-3xl sm:text-4xl font-mono text-emerald-300 mt-1" data-testid="try-tokens-saved">
                    {fmtTokens(result.savings.tokens_saved)}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {fmtPct(result.savings.tokens_saved_pct)} reduction
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-300/80">Dollars saved / call</div>
                  <div className="text-3xl sm:text-4xl font-mono text-emerald-300 mt-1" data-testid="try-dollars-saved">
                    {fmtUsd6(result.savings.dollars_saved)}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {fmtPct(result.savings.dollars_saved_pct)} cheaper
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-300/80">At 1M calls / month</div>
                  <div className="text-3xl sm:text-4xl font-mono text-emerald-300 mt-1" data-testid="try-monthly-saved">
                    {fmtUsd6(result.savings.dollars_saved * 1_000_000)}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    extrapolated · just this prompt
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Techniques applied */}
          <div className="grid md:grid-cols-3 gap-3 mb-6" data-testid="try-techniques">
            {result.techniques_applied.map((t, i) => {
              const Icon = i === 0 ? Code : i === 1 ? Database : Cpu;
              return (
                <Card key={t.name} className={`bg-slate-900/40 ring-1 ${t.always_on ? 'ring-emerald-500/40' : 'ring-slate-800'}`}
                      data-testid={`try-technique-${i + 1}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <Icon className={`w-4 h-4 mt-0.5 ${t.always_on ? 'text-emerald-300' : 'text-slate-400'}`} />
                      <div className="text-sm font-medium text-slate-100">{t.name}</div>
                      {t.always_on ? (
                        <span className="ml-auto inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">
                          <Check className="w-2.5 h-2.5 mr-0.5" />FIRED
                        </span>
                      ) : (
                        <span className="ml-auto inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 font-bold">
                          PROD AVG
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 leading-relaxed">{t.what_it_did}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Transparency note */}
          <Card className="bg-slate-900/40 ring-1 ring-slate-800 mb-6" data-testid="try-transparency">
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Honest math</div>
              <p className="text-sm text-slate-300 leading-relaxed">{result.transparency_note}</p>
            </CardContent>
          </Card>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3" data-testid="try-cta-row">
            <Link to="/pricing" className="flex-1">
              <Button size="lg" className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold"
                      data-testid="try-cta-start-trial">
                Start 14-day free trial <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/engine-27" className="flex-1">
              <Button size="lg" variant="outline" className="w-full border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                      data-testid="try-cta-engine27">
                See arbitrage engine internals <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* ── Footer block ── */}
      <section className="max-w-3xl mx-auto px-6 py-16 border-t border-slate-900 text-center">
        <h3 className="text-2xl font-medium mb-3">Drop-in. Production-grade. Reversible.</h3>
        <p className="text-sm text-slate-400 mb-6">
          The same compactor you just saw runs on every paying customer&apos;s LLM call.
          One environment variable to switch on — one to roll back.
        </p>
        <Link to="/pricing#pledge">
          <Button variant="outline" className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10">
            3× ROI in 90 days or full refund · See the contract <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
